/**
 * Post List Item Component
 * 
 * Displays a single GMB post in the list with actions
 */

import React from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Eye, 
  MousePointer, 
  MapPin, 
  Calendar, 
  Image as ImageIcon,
  Edit,
  Trash2,
  Tag,
  Link,
  Clock
} from 'lucide-react'
import type { GmbPost, GmbLocation } from '@/features/gmb/types'

interface PostListItemProps {
  post: GmbPost
  locations: GmbLocation[]
  onEdit: (post: GmbPost) => void
  onDelete: (postId: string) => void
}

export function PostListItem({
  post,
  locations,
  onEdit,
  onDelete
}: PostListItemProps) {
  // Find the location name
  const location = locations.find(l => l.gmb_location_id === post.gmb_location_id)
  const locationName = location?.location_name || post.gmb_location_id

  // Get post type icon
  const getPostTypeIcon = () => {
    switch (post.topic_type) {
      case 'EVENT':
        return <Calendar className="h-4 w-4" />
      case 'OFFER':
        return <Tag className="h-4 w-4" />
      case 'PRODUCT':
        return <ImageIcon className="h-4 w-4" />
      default:
        return null
    }
  }

  // Get status badge variant
  const getStatusBadgeVariant = () => {
    switch (post.state) {
      case 'LIVE':
        return 'default'
      case 'SCHEDULED':
        return 'secondary'
      case 'DRAFT':
        return 'outline'
      case 'REJECTED':
        return 'destructive'
      case 'REMOVED':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  // Format the post summary for display
  const truncatedSummary = post.summary && post.summary.length > 200 
    ? post.summary.substring(0, 200) + '...' 
    : post.summary || ''

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left side - Post content */}
          <div className="flex-1 space-y-3">
            {/* Header */}
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant={getStatusBadgeVariant()}>
                {post.state}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                {getPostTypeIcon()}
                {post.topic_type}
              </Badge>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="h-3 w-3" />
                {locationName}
              </div>
            </div>

            {/* Title (for events) */}
            {post.event_title && (
              <h3 className="font-semibold">{post.event_title}</h3>
            )}

            {/* Summary */}
            <p className="text-sm">{truncatedSummary}</p>

            {/* Media indicators */}
            {post.media_urls && post.media_urls.length > 0 && (
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {post.media_urls.length} image{post.media_urls.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}

            {/* CTA */}
            {post.cta_type && (
              <div className="flex items-center gap-2">
                <Link className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  <span className="font-medium">{post.cta_type.replace(/_/g, ' ')}</span>
                  {post.cta_url && (
                    <span className="text-muted-foreground ml-1">
                      → {new URL(post.cta_url).hostname}
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Metadata */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              {/* Date */}
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {post.scheduled_for && post.state === 'SCHEDULED' ? (
                  <>Scheduled for {format(new Date(post.scheduled_for), 'MMM d, yyyy h:mm a')}</>
                ) : post.gmb_created_at ? (
                  <>Published {formatDistanceToNow(new Date(post.gmb_created_at))} ago</>
                ) : (
                  <>Created {formatDistanceToNow(new Date(post.created_at))} ago</>
                )}
              </div>

              {/* Performance metrics */}
              {post.state === 'LIVE' && (
                <>
                  <div className="flex items-center gap-1">
                    <Eye className="h-3 w-3" />
                    {(post.view_count || 0).toLocaleString()} views
                  </div>
                  <div className="flex items-center gap-1">
                    <MousePointer className="h-3 w-3" />
                    {(post.click_count || 0).toLocaleString()} clicks
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right side - Thumbnail and actions */}
          <div className="flex flex-col items-end gap-2">
            {/* Thumbnail - handles both sourceUrl (dashboard posts) and googleUrl (synced GMB posts) */}
            {post.media_urls && post.media_urls.length > 0 && (post.media_urls[0].sourceUrl || post.media_urls[0].googleUrl) && (
              <div className="w-24 h-24 rounded-lg overflow-hidden bg-muted">
                <img
                  src={post.media_urls[0].sourceUrl || post.media_urls[0].googleUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onEdit(post)}
                disabled={post.state === 'LIVE'}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDelete(post.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Event dates */}
        {post.topic_type === 'EVENT' && post.event_start_date && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="h-3 w-3" />
              Event: {format(new Date(post.event_start_date), 'MMM d, yyyy')}
              {post.event_end_date && post.event_end_date !== post.event_start_date && (
                <> - {format(new Date(post.event_end_date), 'MMM d, yyyy')}</>
              )}
            </div>
          </div>
        )}

        {/* Offer details */}
        {post.topic_type === 'OFFER' && (post.offer_coupon_code || post.offer_redeem_url) && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center gap-3 text-sm">
              {post.offer_coupon_code && (
                <Badge variant="secondary">
                  Code: {post.offer_coupon_code}
                </Badge>
              )}
              {post.offer_terms && (
                <span className="text-muted-foreground text-xs">
                  {post.offer_terms}
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default PostListItem